'use client';

import React, { useMemo } from 'react';
import Image from 'next/image';
import { useBuilderArticles } from '@/hooks/useBuilderArticles';
import { BuilderArticlePageReference, ArticlePreview } from '@/types/article';
import { BuilderState } from '@/types/builder';
import { CategoryLabel, getCategoryVariant } from '../CategoryLabel/CategoryLabel';
import { host } from '@/config';

export const HUB_FEATURED_ARTICLES_NAME = 'HubFeaturedArticles';
const HUB_FEATURED_ARTICLES_DEFAULT_LIMIT = 4;

interface HubFeaturedArticlesProps {
  title?: string;
  showDate?: boolean;
  featuredExcerpt?: string;
  featuredLabel?: string;
  autoSelectLatest?: boolean;
  limit?: number;
  articles?: { article?: BuilderArticlePageReference }[];
  builderState: BuilderState;
}

const getCurrentMonth = () => new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(new Date());

const FeaturedHeroCard: React.FC<{
  article: ArticlePreview;
  excerpt?: string;
  label?: string;
}> = ({ article, excerpt, label = 'Featured' }) => {
  const { title, heroImage, url } = article;

  return (
    <div className="border-background-400 shadow-tc flex flex-col gap-4 rounded-lg border bg-white p-4">
      <div className="relative">
        {heroImage && (
          <div className="relative h-[125px] w-full overflow-hidden rounded-md md:h-[272px]">
            <Image
              src={heroImage}
              alt={title || ''}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 60vw"
              priority
            />
          </div>
        )}
        <span className="bg-primary text-neutral absolute top-[10px] left-[10px] inline-flex items-center rounded-[6px] border-2 border-white px-4 py-2 text-[14px] font-bold">
          {label}
        </span>
      </div>

      <p className="text-dark my-0 text-[18px] leading-[1.2] font-bold">{title}</p>

      {excerpt && <p className="text-dark my-0 text-[16px] leading-[1.4] md:text-[18px]">{excerpt}</p>}

      <div>
        <a
          href={`${host}${url}`}
          className="border-dark-100 text-dark-200 bg-neutral hover:border-primary hover:shadow-primary/10 active:bg-background-300 inline-flex h-[50px] w-[237px] items-center justify-center rounded-md border text-[16px] font-bold transition-all hover:shadow-xl active:shadow-none"
        >
          Read more
        </a>
      </div>
    </div>
  );
};

const OverlayCard: React.FC<{ article: ArticlePreview }> = ({ article }) => {
  const { title, heroImage, url, category } = article;
  const categoryTitle = category?.value?.data?.title;

  return (
    <a
      href={`${host}${url}`}
      className="border-background-400 shadow-tc relative flex h-full min-h-[140px] flex-col justify-between overflow-hidden rounded-lg border p-4 md:min-h-[180px]"
    >
      {heroImage && (
        <Image src={heroImage} alt={title || ''} fill className="object-cover" sizes="(max-width: 768px) 100vw, 33vw" />
      )}
      <div className="pointer-events-none absolute inset-0 rounded-lg bg-linear-to-b from-transparent to-black" />

      <div className="relative z-10">
        {categoryTitle && (
          <CategoryLabel
            category={category!.value!}
            variant={getCategoryVariant(categoryTitle)}
            className="rounded-[6px]! border-2! border-white! px-4! py-2!"
          />
        )}
      </div>

      <p className="text-neutral relative z-10 my-0 text-[18px] leading-[1.2] font-bold">{title}</p>
    </a>
  );
};

export const HubFeaturedArticles: React.FC<HubFeaturedArticlesProps> = ({
  title,
  showDate = true,
  featuredExcerpt,
  featuredLabel = 'Featured',
  autoSelectLatest = true,
  limit = HUB_FEATURED_ARTICLES_DEFAULT_LIMIT,
  articles: articleInputs = [],
  builderState,
}) => {
  const articles = useBuilderArticles({
    autoSelectLatest,
    limit,
    latestArticles: builderState.state.featuredArticles,
    articleInputs,
    categories: builderState.state.categories,
  });

  const currentMonth = useMemo(() => getCurrentMonth(), []);

  if (!articles.length) return null;

  const [featured, ...subArticles] = articles;

  return (
    <div className="flex flex-col gap-4">
      {title && (
        <div className="my-0 flex flex-wrap items-baseline text-[22px] leading-[1.2] font-bold *:m-0 md:text-[28px]">
          <div className="contents" dangerouslySetInnerHTML={{ __html: title }} />
          {showDate && <span>&nbsp;{currentMonth}</span>}
        </div>
      )}

      <FeaturedHeroCard article={featured} excerpt={featuredExcerpt} label={featuredLabel} />

      {subArticles.length > 0 && (
        <div className="flex flex-col gap-4 md:flex-row">
          {subArticles.map((article, index) => (
            <div key={index} className="flex-1">
              <OverlayCard article={article} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
